/* The theatre's hero — KANON `Cwiczenia` 17.09.2026, `2-karta.html#e`'s `hero()` at its variant-e
 * settings: no name on the picture (the bar above carries it), no time chip (deviation 2), a scrim
 * rising from the foot with the type mark and the „Twoje" badge on it.
 *
 * The box is settled by the PANEL: `flex: 1` over a floor, so the picture takes whatever the
 * similar strip under it leaves and `object-fit: cover` frames it. Nothing here depends on the
 * file's own dimensions, which is what makes a slow photograph a change of ink.
 *
 * Every text leaf states its line-height. */

.hero {
  position: relative;
  flex: 1;
  min-height: var(--h-ex-theatre-hero);
  overflow: hidden;
  background: var(--bg-sunken);
  border-radius: var(--radius-sm);
}

/* Both fillers draw the same box. `object-fit: cover` is `photoBox`'s on the `<img>`; the `<video>`
 * states its own, because it is not an `<img>` and does not inherit that stylesheet. */
.still,
.film {
  position: absolute;
  inset: var(--space-0);
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--bg-sunken);
}

/* No photograph and no film. A drawn tile and a word, never an empty box — an empty box on a card
 * this size reads as a render that failed. */
.tile {
  position: absolute;
  inset: var(--space-0);
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  align-items: center;
  justify-content: center;
  color: var(--text-3);
}

.tileGlyph {
  display: block;
  width: 76px;
  height: 76px;
}

/* A failed upload's sentence, in the tile's place. Readable ink (`--text-2`), not the ornament's
 * `--text-3`: it is a sentence the trainer has to read. */
.tileNote {
  max-width: 36ch;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-note);
  color: var(--text-2);
  text-align: center;
}

/* The same sentence over a picture: the badge's white plate, in the corner the scrim leaves free.
 * Theme-independent for the badge's reason — it sits on a photograph. A caption, never a target:
 * the hero's own controls stay clickable under it. */
.plateNote {
  position: absolute;
  pointer-events: none;
  top: var(--space-4);
  left: var(--space-5);
  max-width: 36ch;
  padding: var(--space-6px) var(--space-2);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-note);
  color: var(--text-on-plate);
  background: var(--plate-on-photo);
  border-radius: var(--radius-xs);
}

.tileWord {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--text-3);
}

/* ── The scrim ──────────────────────────────────────────────────────────────────────────────── */

/* `pointer-events: none`, so the band cannot swallow a press on the browser's own control strip
 * underneath it. The play button takes its own back — see `.play`. */
.scrim {
  position: absolute;
  right: var(--space-0);
  bottom: var(--space-0);
  left: var(--space-0);
  display: flex;
  gap: var(--space-14px);
  align-items: flex-end;
  padding: var(--space-4) var(--space-5);
  pointer-events: none;
  background: var(--scrim-ex-theatre);
}

/* Once the film is running the browser's own strip owns the bottom of the box, so the scrim's band
 * gets out of its way rather than painting a gradient and a type mark over a seek bar. */
.scrimQuiet {
  display: none;
}

.scrimText {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

/* The type's own hue and its own glyph, HERE TOO — owner decision D17, 18 September 2026. The
 * specimen printed this one mark white, and this sheet did until the owner asked for one mark in
 * one colour across the screen. The hue reads because it sits on the scrim's gradient, not on the
 * photograph itself, and the 13px glyph beside the word is the second channel the dot never was. */
.typeMark {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-block);
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--type-ink);
}

.typeGlyph {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--type-ink);
}

/* The badge INVERTS on the scrim: a white plate with near-black ink, where the same badge on a flat
 * surface is `--bg-chip` with `--text`. The theme's chip fill is invisible over a photograph. */
.badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 18px;
  padding: var(--space-0) var(--space-7px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-cat);
  white-space: nowrap;
  color: var(--text-on-plate);
  background: var(--plate-on-photo);
  border-radius: var(--radius-xs);
}

/* The only thing in the scrim that takes a press back. */
.play {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--sz-ex-theatre-play);
  height: var(--sz-ex-theatre-play);
  margin-bottom: var(--space-2px);
  line-height: var(--lh-none);
  pointer-events: auto;
  cursor: pointer;
  color: var(--text-on-plate);
  background: var(--plate-on-photo);
  border: none;
  border-radius: var(--r-pill);
}

/* The ring is white because the plate is: `--accent` on a white disc over a dark photograph is the
 * one focus treatment on this surface that would disappear, and a coloured glow is banned outright. */
.play:focus-visible {
  outline: var(--border-focus) solid var(--text-on-photo);
  outline-offset: var(--space-2px);
}

.playGlyph {
  display: block;
  width: 15px;
  height: 15px;

  /* The triangle's optical centre is right of its bounding box's. */
  margin-left: var(--space-2px);
}
